package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ApiTokenDto
import dev.lingvo.shared.CreateTokenRequest
import dev.lingvo.shared.CreatedTokenDto
import dev.lingvo.shared.TokenScope
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LCopyIcon
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.copySafely
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.pages.app.projects.slug.settings.SettingsFormMeasure
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Page("/app/settings/tokens")
@Layout(".components.layouts.AppLayout")
@Composable
fun TokensPage() {
    var refreshKey by remember { mutableStateOf(0) }
    var createdToken by remember { mutableStateOf<CreatedTokenDto?>(null) }
    var pendingRevoke by remember { mutableStateOf<ApiTokenDto?>(null) }
    // `loadReloadable`, not `loadAsync(refreshKey)`. Passing the counter as an identity key threw
    // the list away before the re-read was even sent, so creating a token replaced "Your tokens"
    // with skeleton rows at the moment the reader wanted to see their new row appear — and
    // revoking destroyed the row whose Revoke button had just been pressed.
    val reloadable = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) {
        ApiClient.listApiTokens()
    }
    val toasts = rememberToasts()

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            PageHeader(
                t("app.settings.tokens.api_tokens", "API tokens"),
                t("app.settings.tokens.create_credentials_for_the_lingvo_cli", "Create credentials for the Lingvo CLI and trusted automation."),
                mark = { LKeyIcon() },
                actions = {
                    // Secondary, and named for what it actually does. The create form is already
                    // mounted below with its own accent "Create token" submit, so an identical
                    // accent button in the header made two primaries on one screen with the same
                    // label — and the louder of the two created nothing: it moves the caret.
                    LButton(
                        t("app.settings.tokens.name_a_token", "Name a token"),
                        { (document.querySelector("#token-create input") as? HTMLElement)?.focus() },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LPlusIcon() },
                    )
                },
            )
            CreateTokenCard(
                token = createdToken,
                onCreated = { token ->
                    createdToken = token
                    refreshKey++
                },
                onAcknowledged = { createdToken = null },
                notify = toasts::show,
            )
            LSectionHeading(t("app.settings.tokens.your_tokens", "Your tokens"), t("app.settings.tokens.only_prefixes_are_retained_after_creation", "Only prefixes are retained after creation. Revoke credentials you no longer use."))
            // Beside the retained list rather than in place of it: a re-read that fails after a
            // revoke leaves the last good list on screen, and saying so is the difference between
            // "this is stale" and "your revoke did not take".
            reloadable.refreshError?.let { failure ->
                LAlert(
                    t("app.settings.tokens.showing_the_last_list_that_loaded", "Showing the last list that loaded"),
                    failure.readable(),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }
            LAsyncContent(reloadable.state, rows = 3, onRetry = { refreshKey++ }) { tokens ->
                TokenList(tokens, onRevoke = { pendingRevoke = it })
            }
            CliSnippet()
        }
    }
    RevokeModal(
        token = pendingRevoke,
        onClose = { pendingRevoke = null },
        onRevoked = {
            pendingRevoke = null
            refreshKey++
        },
        notify = toasts::show,
    )
    ToastHost(toasts)
}

@Composable
private fun CreateTokenCard(
    token: CreatedTokenDto?,
    onCreated: (CreatedTokenDto) -> Unit,
    onAcknowledged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    var name by remember { mutableStateOf("") }
    var creating by remember { mutableStateOf(false) }
    // Read-only to start with. The common first token runs `lingvo qa` in CI, and a default that
    // could write is a default that eventually does.
    var scopes by remember { mutableStateOf(TokenScope.DEFAULT) }
    var expiry by remember { mutableStateOf("90") }
    LCard(Modifier.fillMaxWidth().maxWidth(SettingsFormMeasure).attr("id", "token-create")) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            // The full grant — every scope with its consequence, and the summary sentence — lives on
            // its own route, because a token grant is a decision with a URL somebody should be able
            // to send to their security team.
            Link(path = "/app/settings/tokens/new") { Text(t("app.settings.tokens.open_the_full_form", "Open the full form")) }
            LSectionHeading(
                t("app.settings.tokens.create_a_token", "Create a token"),
                t("app.settings.tokens.give_each_device_or_workflow_its", "Give each device or workflow its own credential, with only the permissions it " +
                    "needs, so it can be revoked independently and cannot do more than its job."),
            )
            Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.Bottom) {
                LTextField(
                    name,
                    { name = it },
                    Modifier.styleModifier { property("flex", "1 1 260px") },
                    label = t("app.settings.tokens.token_name", "Token name"),
                    placeholder = t("app.settings.tokens.release_automation", "Release automation"),
                    enabled = token == null && !creating,
                )
                LButton(
                    t("app.settings.tokens.create_token", "Create token"),
                    {
                        scope.launch {
                            creating = true
                            try {
                                val created = ApiClient.createApiToken(
                                    CreateTokenRequest(
                                        name = name.trim(),
                                        scopes = scopes.sortedBy { it.ordinal },
                                        expiresInDays = expiry.toIntOrNull(),
                                    ),
                                )
                                name = ""
                                onCreated(created)
                                notify(tr("app.settings.tokens.api_token_created", "API token created"), LToastVariant.SUCCESS, 4_000)
                            } catch (cancelled: CancellationException) {
                                // Navigating away from a half-finished create is not a failure to
                                // report: swallowed here it became a red toast over a dead screen.
                                throw cancelled
                            } catch (failure: Throwable) {
                                notify(
                                    failure.safeMessage(
                                        tr(
                                            "app.settings.tokens.couldnt_create_this_token_retry",
                                            "Couldn't create this token. Retry?",
                                        ),
                                    ),
                                    LToastVariant.DANGER,
                                    TOAST_UNTIL_DISMISSED,
                                )
                            } finally {
                                creating = false
                            }
                        }
                    },
                    enabled = name.isNotBlank() && token == null,
                    loading = creating,
                )
            }
            // Permissions before the button, because they are the decision. A token cannot touch
            // people, money, provider keys or other tokens under any of these — there is no scope
            // for those, and there never will be, which is why they are not on this list.
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LSectionTitle(
                    t("app.settings.tokens.what_this_token_may_do", "What this token may do"),
                    Modifier.fontSize(TypeScale.BodySmall),
                )
                TokenScope.entries.forEach { scopeOption ->
                    LCheckbox(
                        checked = scopeOption in scopes,
                        onCheckedChange = { checked ->
                            scopes = if (checked) scopes + scopeOption else scopes - scopeOption
                        },
                        label = tf("app.settings.tokens.scope_and_description", "{scope} — {description}", "scope" to scopeOption.id, "description" to scopeOption.description),
                        enabled = token == null && !creating,
                    )
                }
                LSelect(
                    value = expiry,
                    options = listOf(
                        LSelectOption("30", t("app.settings.tokens.expires_in_30_days", "Expires in 30 days")),
                        LSelectOption("90", t("app.settings.tokens.expires_in_90_days", "Expires in 90 days")),
                        LSelectOption("365", t("app.settings.tokens.expires_in_a_year", "Expires in a year")),
                        // Offered, but not the default: a credential nobody ever has to think about
                        // again is a credential nobody ever revokes.
                        LSelectOption("", t("app.settings.tokens.never_expires", "Never expires")),
                    ),
                    onValueChange = { expiry = it },
                    label = t("app.settings.tokens.expiry", "Expiry"),
                    enabled = token == null && !creating,
                )
            }
            if (token != null) OneTimeTokenCard(token, onAcknowledged, notify)
        }
    }
}

@Composable
private fun OneTimeTokenCard(
    token: CreatedTokenDto,
    onAcknowledged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    var copyFailure by remember { mutableStateOf<String?>(null) }
    LCard(Modifier.fillMaxWidth().border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().warning)) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            LAlert(
                t("app.settings.tokens.copy_this_token_now", "Copy this token now"),
                t("app.settings.tokens.lingvo_cannot_show_the_full_token", "Lingvo cannot show the full token again after you dismiss this card."),
                variant = LAlertVariant.WARNING,
            )
            LOneTimeSecret(
                value = token.token,
                label = tr("app.settings.tokens.new_api_token", "New API token"),
                copyLabel = t("app.settings.tokens.copy_token", "Copy token"),
                onCopied = {
                    copyFailure = null
                    notify(tr("app.settings.tokens.token_copied", "Token copied"), LToastVariant.SUCCESS, 4_000)
                },
                // Inline, and it names the object and the deadline — the agent cards' sentence.
                // A corner toast that clears itself after six seconds is the wrong carrier for
                // "select this and copy it by hand", and "it" had no antecedent from over there.
                onCopyFailed = {
                    copyFailure = tr(
                        "app.settings.tokens.couldnt_reach_the_clipboard_select_the",
                        "Couldn't reach the clipboard. Select the token above and copy it " +
                            "manually before leaving.",
                    )
                },
                trailingAction = {
                    LButton(
                        t("app.settings.tokens.i_have_saved_it", "I have saved it"),
                        onAcknowledged,
                        variant = LButtonVariant.SECONDARY,
                    )
                },
            )
            // Reached only by pressing copy and having it refused, so it is announced.
            copyFailure?.let {
                LAlert(
                    t("app.settings.tokens.copy_it_by_hand", "Copy it by hand"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }
        }
    }
}

@Composable
private fun TokenList(tokens: List<ApiTokenDto>, onRevoke: (ApiTokenDto) -> Unit) {
    if (tokens.isEmpty()) {
        LEmptyState(
            t("app.settings.tokens.no_api_tokens", "No API tokens"),
            t("app.settings.tokens.create_a_dedicated_token_before_connecting", "Create a dedicated token before connecting the CLI or an automation workflow."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.KEY,
        ) {
            LButton(
                t("app.settings.tokens.name_a_token", "Name a token"),
                { (document.querySelector("#token-create input") as? HTMLElement)?.focus() },
                variant = LButtonVariant.SECONDARY,
            )
        }
        return
    }
    LTable(caption = t("app.settings.tokens.api_tokens", "API tokens")) {
        LTableHead {
            LTableRow {
                listOf("Name", "Prefix", "Can do", "Expires", "Last used", "Status", "Action").forEach {
                    LTableHeaderCell { Text(it) }
                }
            }
        }
        LTableBody {
            tokens.forEach { token ->
                LTableRow {
                    val revokedAt = token.revokedAt
                    LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                        // **The row's own route.** `/app/settings/tokens/{tokenId}` existed and
                        // nothing in the product linked to it, so the page that shows one token's
                        // scopes, last use and revocation was reachable only by typing a UUID.
                        Link(path = "/app/settings/tokens/${token.id}") { Text(token.name) }
                    }
                    LTableCell { Span(Modifier.monoFont().toAttrs()) { Text(token.prefix) } }
                    LTableCell {
                        // Spelled out rather than counted. "3 scopes" tells nobody whether this is
                        // the token that can spend money.
                        Text(
                            if (token.scopes.isEmpty()) t("app.settings.tokens.nothing", "Nothing")
                            else token.scopes.joinToString(", ") { it.id },
                        )
                    }
                    LTableCell {
                        when {
                            token.expiresAt == null -> Text(t("app.settings.tokens.never", "Never"))
                            // Says why, because nobody chose this date.
                            token.isLegacy -> LBadge(
                                formatTimestamp(token.expiresAt!!) + " — made before scopes",
                                variant = LBadgeVariant.WARNING,
                            )
                            else -> Text(formatTimestamp(token.expiresAt!!))
                        }
                    }
                    LTableCell { Text(token.lastUsedAt?.let(::formatTimestamp) ?: "Never") }
                    LTableCell {
                        LBadge(
                            if (revokedAt == null) t("app.settings.tokens.active", "Active") else t("app.settings.tokens.revoked", "Revoked"),
                            variant = if (revokedAt == null) LBadgeVariant.SUCCESS else LBadgeVariant.NEUTRAL,
                        )
                    }
                    LTableCell {
                        if (revokedAt == null) {
                            // Every row's button reads "Revoke". The name column is what separates
                            // them, and a screen reader hearing the eighth "Revoke" has no way back
                            // to which credential it belongs to.
                            LButton(
                                t("app.settings.tokens.revoke", "Revoke"),
                                { onRevoke(token) },
                                variant = LButtonVariant.DANGER,
                                size = LButtonSize.SM,
                                accessibleLabel = tokenRowRevokeLabel(token.name),
                            )
                        } else {
                            Span(Modifier.color(ColorMode.current.toSitePalette().textMuted).fontSize(TypeScale.Small).toAttrs()) {
                                Text(formatTimestamp(revokedAt))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RevokeModal(
    token: ApiTokenDto?,
    onClose: () -> Unit,
    onRevoked: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    var revoking by remember { mutableStateOf(false) }
    LModal(token != null, t("app.settings.tokens.revoke_api_token", "Revoke API token?"), onClose, actions = {
        LButton(t("app.settings.tokens.cancel", "Cancel"), onClose, variant = LButtonVariant.GHOST, enabled = !revoking)
        LButton(
            t("app.settings.tokens.revoke_token", "Revoke token"),
            {
                val current = token ?: return@LButton
                scope.launch {
                    revoking = true
                    try {
                        ApiClient.deleteApiToken(current.id)
                        notify(tr("app.settings.tokens.api_token_revoked", "API token revoked"), LToastVariant.SUCCESS, 4_000)
                        onRevoked()
                    } catch (cancelled: CancellationException) {
                        // The dialog closing under a revoke in flight cancels this scope; reported
                        // as a failure it told the reader the revoke had gone wrong when it had not.
                        throw cancelled
                    } catch (failure: Throwable) {
                        notify(
                            failure.safeMessage(
                                tr(
                                    "app.settings.tokens.couldnt_revoke_this_token_retry",
                                    "Couldn't revoke this token. Retry?",
                                ),
                            ),
                            LToastVariant.DANGER,
                            TOAST_UNTIL_DISMISSED,
                        )
                    } finally {
                        revoking = false
                    }
                }
            },
            variant = LButtonVariant.DANGER,
            loading = revoking,
        )
    }) {
        LAlert(
            t("app.settings.tokens.access_will_stop_immediately", "Access will stop immediately"),
            tf(
                "app.settings.tokens.token_will_no_longer_authenticate",
                "{name} ({prefix}) will no longer authenticate CLI or API requests.",
                "name" to token?.name.orEmpty(),
                "prefix" to token?.prefix.orEmpty(),
            ),
            variant = LAlertVariant.DANGER,
        )
    }
}

@Composable
private fun CliSnippet() {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            LSectionHeading(t("app.settings.tokens.use_a_token_with_the_cli", "Use a token with the CLI"), t("app.settings.tokens.set_the_token_only_for_the", "Set the token only for the shell or secret store that needs it."))
            LCodeBlock(
                text = "export LINGVO_TOKEN=\"lgo_...\"\nlingvo whoami\nlingvo status",
                label = t("app.settings.tokens.shell", "Shell"),
                copyable = false,
            )
        }
    }
}


@Composable
private fun codeBlockModifier(): Modifier = Modifier
    .fillMaxWidth()
    .overflow(Overflow.Auto)
    .backgroundColor(ColorMode.current.toSitePalette().surfaceSunken)
    .border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().border)
    .borderRadius(Radius.Control)
    .padding(Space.LG)
    .margin(0.px)
    .monoFont()
    .fontSize(TypeScale.BodySmall)
    .lineHeight(TypeScale.BodyLineHeight)

private fun formatTimestamp(value: String): String {
    val normalized = value.replace('T', ' ').substringBefore('.')
    return normalized.removeSuffix("Z").take(16).ifBlank { value }
}

/**
 * The server's sentence, or this page's own — never an internal one.
 *
 * Cast first, the way `Keys.kt` and `Glossary.kt` do: a `check`/`require` failure inside a
 * mutation lambda is a `Throwable` with a message written for a developer, and the unguarded form
 * put "Required value was null." in front of the reader.
 */
private fun Throwable.safeMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback

/**
 * What one credential's Revoke button is called to a screen reader.
 *
 * `"${t("…revoke", "Revoke")} ${token.name}"` translated the verb and left the join in English word
 * order — "Widerrufen ci-deploy-token" — which no locale that needs the object first can express,
 * and the template was invisible to the extractor's parameter check.
 */
internal fun tokenRowRevokeLabel(name: String): String = trf(
    "app.settings.tokens.revoke_name",
    "Revoke {name}",
    "name" to name,
)
